import type { Vec2 } from "@air-defence-sim/sim-core";
import type { Palette } from "./theme.js";

export function drawTarget(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  value: number,
  destroyed: boolean,
  isCapital: boolean,
  isMajor: boolean,
  palette: Palette,
): void {
  const r = 4 + Math.sqrt(Math.max(0, value)) / 4;
  let colour: string;
  if (destroyed) colour = palette.targetDead;
  else if (isCapital) colour = palette.targetCapital;
  else if (isMajor) colour = palette.targetMajor;
  else colour = palette.target;
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = palette.targetMarker;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  if (isCapital) {
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, Math.max(2, r - 2.5), 0, Math.PI * 2);
    ctx.strokeStyle = palette.targetMarker;
    ctx.lineWidth = 0.9;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fillStyle = palette.targetMarker;
    ctx.fill();
  }
  if (destroyed) {
    ctx.strokeStyle = "#7a4a4a";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pos.x - r, pos.y - r);
    ctx.lineTo(pos.x + r, pos.y + r);
    ctx.moveTo(pos.x + r, pos.y - r);
    ctx.lineTo(pos.x - r, pos.y + r);
    ctx.stroke();
  }
}

export function drawFixedDefence(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  palette: Palette,
): void {
  const s = 5.5;
  ctx.fillStyle = palette.fixed;
  ctx.fillRect(pos.x - s, pos.y - s, s * 2, s * 2);
  ctx.strokeStyle = palette.hull;
  ctx.lineWidth = 1;
  ctx.strokeRect(pos.x - s, pos.y - s, s * 2, s * 2);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pos.x - s + 1.5, pos.y - s + 1.5);
  ctx.lineTo(pos.x + s - 1.5, pos.y + s - 1.5);
  ctx.moveTo(pos.x + s - 1.5, pos.y - s + 1.5);
  ctx.lineTo(pos.x - s + 1.5, pos.y + s - 1.5);
  ctx.stroke();
  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
  ctx.beginPath();
  ctx.arc(pos.x, pos.y, 1.4, 0, Math.PI * 2);
  ctx.fill();
}

export function drawMobileDefence(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  heading: number | null,
  palette: Palette,
): void {
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(heading ?? 0);
  ctx.fillStyle = palette.mobile;
  ctx.beginPath();
  ctx.moveTo(8, 0);
  ctx.lineTo(-5, 5);
  ctx.lineTo(-3, 0);
  ctx.lineTo(-5, -5);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = palette.hull;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

export function drawSensor(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  palette: Palette,
): void {
  ctx.strokeStyle = palette.sensor;
  ctx.fillStyle = palette.sensor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(pos.x, pos.y - 1, 4, Math.PI, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(pos.x - 5, pos.y - 1);
  ctx.lineTo(pos.x + 5, pos.y - 1);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(pos.x, pos.y - 1);
  ctx.lineTo(pos.x, pos.y + 4);
  ctx.stroke();
  ctx.fillStyle = palette.sensor;
  ctx.fillRect(pos.x - 3, pos.y + 4, 6, 1.5);
}

export function drawMissile(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  heading: number,
  fade: number,
  palette: Palette,
): void {
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(heading);
  ctx.globalAlpha = Math.max(0, Math.min(1, fade));
  ctx.fillStyle = palette.missile;
  ctx.beginPath();
  ctx.moveTo(4, 0);
  ctx.lineTo(-2, 1.5);
  ctx.lineTo(-1, 0);
  ctx.lineTo(-2, -1.5);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "rgba(56, 117, 246, 0.9)";
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.globalAlpha = Math.max(0, Math.min(1, fade)) * 0.55;
  ctx.strokeStyle = palette.missileTrail;
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(-2, 0);
  ctx.lineTo(-7, 0);
  ctx.stroke();
  ctx.restore();
}

export function drawStrikeWeapon(
  ctx: CanvasRenderingContext2D,
  pos: Vec2,
  heading: number,
  detected: boolean,
  palette: Palette,
): void {
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(heading);
  ctx.fillStyle = detected ? palette.weapon : palette.weaponHidden;
  ctx.beginPath();
  ctx.moveTo(5, 0);
  ctx.lineTo(-3, 2.5);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-3, -2.5);
  ctx.closePath();
  ctx.fill();
  if (detected) {
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
  ctx.restore();
}
